import { useState, useEffect, useCallback } from 'react';
import {
  Typography,
  TextField,
  Button,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Box,
  CircularProgress,
  Snackbar,
  Radio,
  RadioGroup,
  FormLabel,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { makeStyles } from '@material-ui/core/styles';
import PlayArrow from '@material-ui/icons/PlayArrow';
import { useApi } from '@backstage/core-plugin-api';
import { githubWorkflowApiRef } from '../plugin';
import { WorkflowInputInfo } from '../types';

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(3),
  },
  card: {
    marginBottom: theme.spacing(2),
  },
  formSection: {
    marginBottom: theme.spacing(3),
  },
  inputField: {
    marginBottom: theme.spacing(2),
  },
  actionButtons: {
    marginTop: theme.spacing(2),
    '& > button': {
      marginRight: theme.spacing(1),
    },
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: 200,
  },
}));

interface WorkflowDispatchFormProps {
  owner: string;
  repo: string;
  workflowId: string;
  onSuccess?: (runId: number) => void;
}

export const WorkflowDispatchForm: React.FC<WorkflowDispatchFormProps> = ({ owner, repo, workflowId, onSuccess }) => {
  const classes = useStyles();
  const api = useApi(githubWorkflowApiRef);

  const [workflowInputs, setWorkflowInputs] = useState<WorkflowInputInfo[]>([]);
  const [inputValues, setInputValues] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [inputsLoading, setInputsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');

  const loadWorkflowInputs = useCallback(async () => {
    try {
      setInputsLoading(true);
      const response = await api.getWorkflowInputs(owner, repo, workflowId);
      setWorkflowInputs(response.inputs);

      // Initialize input values with defaults
      const defaults: Record<string, string> = {};
      response.inputs.forEach(input => {
        if (input.default) {
          defaults[input.name] = input.default;
        }
      });
      setInputValues(defaults);
    } catch (err) {
      setError(`Failed to load workflow inputs: ${err}`);
    } finally {
      setInputsLoading(false);
    }
  }, [api, owner, repo, workflowId]);

  // Load workflow inputs when component mounts or workflowId changes
  useEffect(() => {
    if (workflowId) {
      loadWorkflowInputs();
    }
  }, [workflowId, owner, repo, loadWorkflowInputs]);

  const handleInputChange = (inputName: string, value: string) => {
    setInputValues(prev => ({
      ...prev,
      [inputName]: value,
    }));
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError('');

      // Validate required inputs
      const missingRequired = workflowInputs
        .filter(input => input.required && !inputValues[input.name])
        .map(input => input.name);

      if (missingRequired.length > 0) {
        setError(`Missing required inputs: ${missingRequired.join(', ')}`);
        return;
      }

      const response = await api.dispatchWorkflow({
        owner,
        repo,
        workflowId: workflowId,
        inputs: inputValues,
      });

      setSuccess('Workflow dispatched successfully!');

      // Call onSuccess callback with runId if provided
      if (onSuccess && response.runId) {
        onSuccess(response.runId);
      }
    } catch (err) {
      setError(`Failed to dispatch workflow: ${err}`);
    } finally {
      setLoading(false);
    }
  };

  const renderInputField = (input: WorkflowInputInfo) => {
    const value = inputValues[input.name] || '';

    switch (input.type) {
      case 'boolean':
        return (
          <FormControlLabel
            key={input.name}
            control={
              <Checkbox
                checked={value === 'true'}
                onChange={e => handleInputChange(input.name, e.target.checked ? 'true' : 'false')}
                color="primary"
              />
            }
            label={input.description || input.name}
            className={classes.inputField}
          />
        );

      case 'choice':
        // Use radio buttons for 3 or fewer options, dropdown for more
        if (input.options && input.options.length <= 3) {
          return (
            <FormControl key={input.name} component="fieldset" className={classes.inputField}>
              <FormLabel component="legend">
                {input.name} {input.required && '*'}
              </FormLabel>
              <RadioGroup value={value} onChange={e => handleInputChange(input.name, e.target.value)}>
                {input.options.map(option => (
                  <FormControlLabel key={option} value={option} control={<Radio color="primary" />} label={option} />
                ))}
              </RadioGroup>
              {input.description && (
                <Typography variant="caption" color="textSecondary">
                  {input.description}
                </Typography>
              )}
            </FormControl>
          );
        }
        return (
          <FormControl key={input.name} fullWidth className={classes.inputField}>
            <InputLabel>
              {input.description || input.name} {input.required && '*'}
            </InputLabel>
            <Select
              value={value}
              onChange={e => handleInputChange(input.name, e.target.value as string)}
              required={input.required}
            >
              {input.options?.map(option => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );

      case 'number':
        return (
          <TextField
            key={input.name}
            fullWidth
            type="number"
            label={input.description || input.name}
            value={value}
            onChange={e => handleInputChange(input.name, e.target.value)}
            required={input.required}
            className={classes.inputField}
          />
        );

      default: // string, environment
        return (
          <TextField
            key={input.name}
            fullWidth
            label={input.description || input.name}
            value={value}
            onChange={e => handleInputChange(input.name, e.target.value)}
            required={input.required}
            className={classes.inputField}
          />
        );
    }
  };

  return (
    <div className={classes.root}>
      <div className={classes.formSection}>
        {inputsLoading && (
          <Box display="flex" justifyContent="center" py={2}>
            <CircularProgress size={24} />
          </Box>
        )}
        {!inputsLoading && workflowInputs.length === 0 && (
          <Typography variant="body2" color="textSecondary">
            This workflow has no input parameters.
          </Typography>
        )}
        {!inputsLoading && workflowInputs.length > 0 && (
          <div>
            {workflowInputs.map(input => (
              <div key={input.name}>{renderInputField(input)}</div>
            ))}
          </div>
        )}
      </div>

      <div className={classes.actionButtons}>
        <Button
          variant="contained"
          color="primary"
          startIcon={loading ? <CircularProgress size={20} /> : <PlayArrow />}
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? 'Submitting...' : 'Submit'}
        </Button>
      </div>

      <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError('')}>
        <Alert onClose={() => setError('')} severity="error">
          {error}
        </Alert>
      </Snackbar>

      <Snackbar open={!!success} autoHideDuration={4000} onClose={() => setSuccess('')}>
        <Alert onClose={() => setSuccess('')} severity="success">
          {success}
        </Alert>
      </Snackbar>
    </div>
  );
};
